import React, { useEffect, useRef, useState } from "react";

import Actions from "src/components/actionbar";
import { AddAssetsDialog } from "src/components/asset/add";
import { FileService } from "src/utils/file.service";
import Tooltip from "src/components/tooltip";
import { toast } from "react-toastify";

import Thumbnail from "src/components/thumbnail";
import { useDispatch } from "react-redux";
import { createMicroLearning, invalidateMicroLearningCreation } from "src/redux/actions/coaching/microLearning";
import { Asset } from "src/types/assets";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import _ from "lodash";
import cx from "classnames";
import styles from "./seek.module.scss";

 const SeekComponent: React.FC<{query: string; onQueryChange: (value: string) => void;
  editorRef: React.RefObject<HTMLDivElement>; getEditorText: (editor: HTMLDivElement) => string;}> = ({ query, onQueryChange, editorRef, getEditorText }) => {
  const dispatch = useDispatch(); 
  const loading = false;
  const navigate = useNavigate();

  const [showAddAssets, setShowAddAssets] = useState(false);
  const [assets, setAssets] = useState<any>([]);
  const [showAssetHint, setShowAssetHint] = React.useState(false);

  const { creating, created, microLearning } = useSelector(
    (state: any) => {
      const microLearningState = _.get(state.coaching.microLearnings, `creation`);
      const creating = _.get(microLearningState, "creating");
      const microLearning = _.get(microLearningState, "item");
      const created = _.get(microLearningState, "created", false);
      return {
        creating,
        created,
        microLearning,
      };
    },
    _.isEqual,
  );  

  const user = useSelector((state: any) => _.get(state, "users.me", null), _.isEqual);
  const queryRef = useRef(query);
  useEffect(() => {
    queryRef.current = query;  }, [query]);
    useEffect(() => {
  setShowAssetHint(
    query.trim().length > 3 &&
    assets.length === 0
  );
}, [query, assets]);

  const handleSearchQuery = (e: any) => {
    e.preventDefault();
    const currentQuery = editorRef.current
  ? getEditorText(editorRef.current)
  : "";
    if (loading || _.size(currentQuery) === 0 || _.size(assets) === 0) {
      return;
    }

    const assetIds = assets.map((asset: Asset) => asset.id);

    dispatch(createMicroLearning({ query: currentQuery, assets: assetIds }));
  };

  const handleEditorKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSearchQuery(e);
    }
  };

  const handleClearQuery = () => {
  onQueryChange("");
  };

  const handleAddAssets = (e?: React.MouseEvent) => {
    e?.preventDefault();
    setShowAssetHint(false);
    // e?.stopPropagation();
    setShowAddAssets(true);
  };


  const entityAction = (entity: any) => {
    if (entity.entityType === "asset") {
      const fileType = FileService.getFileType(entity.metadata);
      const allowedTypes = [
        "PDF",
        "PPT",
        "PPTX",
        "DOC",
        "DOCX",
        "GOOGLE_SLIDES",  
        "LINK",
      ];
      const disabled = fileType && !_.includes(allowedTypes, fileType);
      return (
        <Tooltip
          content="Only pptx, pdf assets can be added to microlearning"
          placement="auto"
          disabled={!disabled}
        >
          <button className={styles.action}>
            <i
              className={cx(
                styles.icon,
                disabled ? "paperflite-lock" : "paperflite-add",
              )}
            ></i>
          </button>
        </Tooltip>
      );
    }
    return false;
  };

  const assetSelectionFilter = (entity: any) => {
    if (entity.entityType === "asset") {
      const fileType = FileService.getFileType(entity.metadata);
      const allowedTypes = [
        "PDF",
        "PPT",
        "PPTX",
        "DOC",
        "DOCX",
        "GOOGLE_SLIDES",
        "LINK",
      ];
      return _.includes(allowedTypes, fileType);
    }
    return false;
  };

  const handleFinishAddAssets = (assets: any[]) => {
    const invalidAssets = assets.filter((asset) => {
      const fileType = FileService.getFileType(asset.metadata);
      const allowedTypes = [
        "PDF",
        "PPT",
        "PPTX",
        "DOC",
        "DOCX",
        "GOOGLE_SLIDES",
        "LINK",
      ];
      return !_.includes(allowedTypes, fileType);
    });

    if (_.size(invalidAssets) > 0) {
      toast.error("Only pptx, pdf assets can be added to microlearning");
      return false;
    }

    const filteredAssets = assets.filter((asset) => {
      const fileType = FileService.getFileType(asset.metadata);
      const allowedTypes = [
        "PDF",
        "PPT",
        "PPTX",
        "DOC",
        "DOCX",
        "GOOGLE_SLIDES",
        "LINK",
      ];
      return _.includes(allowedTypes, fileType);
    });

    if (_.size(filteredAssets) === 0) {
      toast.error("No valid assets selected. Please select pptx, pdf assets.");
      return false;
    }

    if (_.size(filteredAssets) > 5) {
      toast.error("You can only add a maximum of 5 assets to a microlearning.");
      return false;
    }

    if (filteredAssets.length > 0) {
      setAssets(filteredAssets);
      setShowAssetHint(false);
      handleCloseAddAssets();
    }

    return true;
  };

  const handleCloseAddAssets = () => {
    setShowAddAssets(false);
  };

  const removeAsset = (asset: any) => {
    setAssets((prev) => prev.filter(a => a.id !== asset.id))
  }


  useEffect(() => {
    if (created) {
      navigate(`/coaching/microlearning/${microLearning.id}`);
      dispatch(invalidateMicroLearningCreation());
    }
  }, [created]);

  return (
    <section className={styles.container}>
      <h1 className={styles.heading}>what would you like to learn today,  {user.firstName} ?</h1>

      <form className={styles.searchContainer} onSubmit={handleSearchQuery}>
        <div className={styles.inputContainer}>
          <div className={styles.search}>
            <div className={styles.assets}>
              {(assets || []).map((asset, index) => {
                return (
                  <div key={index} className={styles.asset}>
                    <SeekAssetCard
                      asset={asset}
                      onRemove={() => removeAsset(asset)}
                    />
                  </div>
                );
              })}
            </div>
              <div
                ref={editorRef}
                contentEditable
                suppressContentEditableWarning
                className={styles.chatEditor}
                onInput={() => onQueryChange(editorRef.current? getEditorText(editorRef.current) : "")}
                onKeyDown={handleEditorKeyDown}
                />
            <div className={styles.footer}>
              <Actions.IconButton
                icon="paperflite-add"
                className={cx(styles.addSourceButton, {
                  [styles.showAssetHint]: showAssetHint
                })}
                onClick={handleAddAssets}
                tooltip="Add asset"
                showTooltip={showAssetHint}
              />

              <button
                type="submit"
                disabled={creating}
                className={cx(styles.searchButton, {
                  [styles.active]: _.size(query) > 0 && _.size(assets) > 0,
                  [styles.loading]: creating,
                })}
                onClick={handleSearchQuery}
              >
                {creating ? (
                  <i className="fa-solid fa-spinner fa-spin-pulse" />
                ) : (
                  <i className="paperflite-right-arrow" />
                )}
              </button>
            </div>
          </div>
        </div>
      </form>
      <AddAssetsDialog
        show={showAddAssets}
        selectableEntityTypes={["asset"]}
        allowInternalEntities={true}
        allowBackgroundUpload={false}
        requireOptimizedAssets={true}
        initialEntities={assets}
        entityAction={entityAction}
        selectionFilter={assetSelectionFilter}
        onFinish={handleFinishAddAssets}
        onClose={handleCloseAddAssets}
      />
    </section>
  );
};

interface SeekAssetCardProps {
  asset: any;
  onRemove?: (asset: any) => void;
}

export const SeekAssetCard: React.FC<SeekAssetCardProps> = ({ asset, onRemove }) => {
  const handleRemove = (e: React.MouseEvent) => {
    e.preventDefault(); 
    e.stopPropagation();
    onRemove && onRemove(asset);
  };

  return (
    <div className={styles.card}>
      <div className={styles.icon}>
        <Thumbnail
          media={asset?.icon}
          defaultImage="/images/assets/default_asset.jpg"
          className={styles.thumbnail}
        />

        {onRemove && <button className={styles.remove} onClick={handleRemove}>
          <i className="fa-regular fa-circle-xmark" />
        </button>}

        <div className={styles.overlay} />
      </div>
    </div>
  );
};

export default SeekComponent;
